/**
 * Rounding policy — one versioned, named declaration.
 *
 * Every rounding decision in the engine is recorded here, and the policy
 * identifier is embedded in `CALC_VERSION`. If a future GST change alters
 * rounding, the version changes and every stored document still declares
 * which rules produced it. Documents are never silently re-priced.
 *
 * See design §5.11.
 */
export const ROUNDING_POLICY = Object.freeze({
  id: 'round-v1',

  /**
   * Half-up AWAY FROM ZERO. Not banker's rounding (HALF_EVEN): Indian
   * invoicing practice, and effectively all Indian billing software, rounds
   * 0.5 up. Stated explicitly rather than inherited from Math.round.
   */
  mode: 'HALF_UP_AWAY_FROM_ZERO' as const,

  quantityScale: 1000,
  moneyScale: 1,
  taxBasisPoints: 10_000,
  discountPercentMilli: 100_000,

  /** Rounded per line, then summed. */
  applyPerLine: [
    'lineGross',
    'lineDiscount',
    'cgst',
    'sgst',
    'igst',
    'cess',
  ] as const,

  /** Rounded once, at document level. */
  applyAtDocument: ['invoiceDiscountAllocation', 'tcs', 'roundOff'] as const,

  /**
   * The rule 1.6.x broke. Totals are the sum of ALREADY-ROUNDED line
   * values, never a round of the sum of unrounded values. The signature of
   * getting this wrong is the `cgst: 17743.7286` found in 1.6.x data.
   */
  aggregateMethod: 'SUM_OF_ROUNDED' as const,

  /** A document-level discount is distributed across lines by this method. */
  documentDiscountMethod: 'LARGEST_REMAINDER' as const,

  /** Remainder paise from an odd basis-point rate land in SGST. */
  oddBasisPointRemainder: 'SGST' as const,

  /** Free-of-charge quantity is not taxed. */
  freeQuantityTaxable: false,
});

export type RoundingPolicy = typeof ROUNDING_POLICY;
